import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { fmtDate, fmtEUR } from "@/constants/labels";
import { Building2, Users, Plus, Trash2, Mail, ShieldCheck, X, Edit3 } from "lucide-react";

const ROLE_LABELS = {
  admin_societe: "Admin société",
  responsable_etablissement: "Responsable établissement",
  consultation: "Consultation (lecture seule)",
};

export default function MonOrganisation() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("etablissements");
  const [showEtabModal, setShowEtabModal] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/organisations/mine");
      setData(data);
    } catch (err) {
      toast.error(fmtError(err));
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-[#3E4C59]">Chargement…</div>;
  const isAdmin = data.my_role === "admin_societe";
  const etabById = Object.fromEntries((data.etablissements || []).map(e => [e.id, e]));

  const revokeMember = async (id) => {
    if (!window.confirm("Révoquer l'accès de cet utilisateur ?")) return;
    try { await api.delete(`/organisations/memberships/${id}`); toast.success("Accès révoqué"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };
  const revokeInvite = async (id) => {
    if (!window.confirm("Annuler cette invitation ?")) return;
    try { await api.delete(`/organisations/invitations/${id}`); toast.success("Invitation annulée"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  const Tab = ({ id, label, icon: Icon }) => (
    <button onClick={() => setTab(id)} data-testid={`org-tab-${id}`}
      className={`px-4 py-2 text-sm font-semibold border-b-2 inline-flex items-center gap-2 ${
        tab === id ? "border-[#1B3A5C] text-[#1B3A5C]" : "border-transparent text-[#3E4C59]"
      }`}>
      <Icon size={16} /> {label}
    </button>
  );

  return (
    <div data-testid="mon-organisation-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933] inline-flex items-center gap-2">
            <Building2 size={22} /> {data.organisation.raison_sociale}
          </h1>
          <p className="text-sm text-[#3E4C59]">
            SIREN <span className="font-mono">{data.organisation.siren}</span>
            {" • "}Vous : <span className="font-semibold">{ROLE_LABELS[data.my_role]}</span>
            {data.my_seuil_engagement_ht != null && <> • Seuil {fmtEUR(data.my_seuil_engagement_ht)}</>}
          </p>
        </div>
      </div>

      <div className="mt-4 border-b border-[#E4E7EB] flex flex-wrap">
        <Tab id="etablissements" label={`Établissements (${data.etablissements.length})`} icon={Building2} />
        <Tab id="members" label={`Utilisateurs (${data.members.length})`} icon={Users} />
        <Tab id="invitations" label={`Invitations (${data.invitations.length})`} icon={Mail} />
      </div>

      {tab === "etablissements" && (
        <div className="mt-4">
          {isAdmin && (
            <button onClick={() => setShowEtabModal(true)} className="btn-primary" data-testid="org-add-etab-btn">
              <Plus size={14} /> Nouvel établissement
            </button>
          )}
          <div className="card mt-3 overflow-x-auto">
            <table className="table-compact w-full">
              <thead><tr><th>Nom</th><th>SIRET</th><th>Adresse</th><th>Type</th><th></th></tr></thead>
              <tbody>
                {data.etablissements.map(e => (
                  <tr key={e.id} data-testid={`org-etab-row-${e.id}`}>
                    <td className="font-semibold">{e.nom} {e.is_siege && <span className="badge-muted text-[10px]">Siège</span>}</td>
                    <td className="font-mono text-xs">{e.siret}</td>
                    <td className="text-xs">{[e.adresse, e.code_postal, e.ville].filter(Boolean).join(", ") || "—"}</td>
                    <td className="text-xs">{e.type_structure || "—"}</td>
                    <td>{fmtDate(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "members" && (
        <div className="mt-4 card overflow-x-auto">
          <table className="table-compact w-full">
            <thead><tr><th>E-mail</th><th>Rôle</th><th>Établissement</th><th>Seuil engagement</th><th>Depuis</th><th></th></tr></thead>
            <tbody>
              {data.members.map(m => (
                <tr key={m.id} data-testid={`org-member-row-${m.id}`}>
                  <td>{m.user_email}</td>
                  <td><span className="badge-muted">{ROLE_LABELS[m.role]}</span></td>
                  <td className="text-xs">{etabById[m.etablissement_id]?.nom || "—"}</td>
                  <td>{m.seuil_engagement_ht != null ? fmtEUR(m.seuil_engagement_ht) : <span className="text-[#3E4C59]">Illimité</span>}</td>
                  <td className="text-xs">{fmtDate(m.created_at)}</td>
                  <td className="flex gap-1">
                    {isAdmin && (
                      <>
                        <button onClick={() => setEditMember(m)} className="btn-outline text-xs" data-testid={`org-member-edit-${m.id}`}>
                          <Edit3 size={12} />
                        </button>
                        <button onClick={() => revokeMember(m.id)} className="btn-danger text-xs" data-testid={`org-member-revoke-${m.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "invitations" && (
        <div className="mt-4">
          {isAdmin && (
            <button onClick={() => setShowInvite(true)} className="btn-primary" data-testid="org-invite-btn">
              <Plus size={14} /> Inviter un utilisateur
            </button>
          )}
          <div className="card mt-3 overflow-x-auto">
            <table className="table-compact w-full">
              <thead><tr><th>E-mail</th><th>Rôle</th><th>Établissement</th><th>Seuil</th><th>Expire</th><th></th></tr></thead>
              <tbody>
                {data.invitations.length === 0 && <tr><td colSpan={6} className="text-center text-[#3E4C59]">Aucune invitation en attente</td></tr>}
                {data.invitations.map(i => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{ROLE_LABELS[i.role]}</td>
                    <td className="text-xs">{etabById[i.etablissement_id]?.nom || "—"}</td>
                    <td>{i.seuil_engagement_ht != null ? fmtEUR(i.seuil_engagement_ht) : <span className="text-[#3E4C59]">Illimité</span>}</td>
                    <td className="text-xs">{fmtDate(i.expires_at)}</td>
                    <td>
                      {isAdmin && (
                        <button onClick={() => revokeInvite(i.id)} className="btn-outline text-xs">Annuler</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showEtabModal && <EtabModal siren={data.organisation.siren} onClose={() => setShowEtabModal(false)} onCreated={load} />}
      {showInvite && <InviteModal etablissements={data.etablissements} onClose={() => setShowInvite(false)} onCreated={load} />}
      {editMember && <MemberEditModal member={editMember} etablissements={data.etablissements} onClose={() => setEditMember(null)} onSaved={load} />}
    </div>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-[6px] shadow-lg max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E7EB]">
          <h2 className="font-semibold text-[#1F2933]">{title}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EtabModal({ siren, onClose, onCreated }) {
  const [form, setForm] = useState({ siret: siren, nom: "", adresse: "", code_postal: "", ville: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{14}$/.test(form.siret)) return toast.error("SIRET : 14 chiffres");
    if (form.siret.slice(0, 9) !== siren) return toast.error(`Le SIRET doit commencer par le SIREN de l'organisation (${siren}).`);
    setBusy(true);
    try { await api.post("/organisations/etablissements", form); toast.success("Établissement créé"); onCreated(); onClose(); }
    catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <ModalShell title="Nouvel établissement" onClose={onClose}>
      <form onSubmit={submit} className="p-4 space-y-3">
        <div>
          <label className="label">SIRET (14 chiffres, commence par {siren})</label>
          <input required className="input font-mono" value={form.siret} onChange={s("siret")} data-testid="etab-siret" />
        </div>
        <div><label className="label">Nom de l'établissement</label>
          <input required className="input" value={form.nom} onChange={s("nom")} data-testid="etab-nom" /></div>
        <div><label className="label">Adresse</label><input className="input" value={form.adresse} onChange={s("adresse")} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">Code postal</label><input className="input" value={form.code_postal} onChange={s("code_postal")} /></div>
          <div><label className="label">Ville</label><input className="input" value={form.ville} onChange={s("ville")} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="etab-submit">{busy ? "…" : "Créer"}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function InviteModal({ etablissements, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", role: "responsable_etablissement", etablissement_id: etablissements[0]?.id || "", seuil_engagement_ht: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.etablissement_id) return toast.error("Choisir un établissement");
    setBusy(true);
    try {
      const payload = { ...form, seuil_engagement_ht: form.seuil_engagement_ht ? Number(form.seuil_engagement_ht) : null };
      await api.post("/organisations/invitations", payload);
      toast.success(`Invitation envoyée à ${form.email}`); onCreated(); onClose();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <ModalShell title="Inviter un utilisateur" onClose={onClose}>
      <form onSubmit={submit} className="p-4 space-y-3">
        <div><label className="label">Adresse e-mail</label>
          <input required type="email" className="input" value={form.email} onChange={s("email")} data-testid="invite-email" /></div>
        <div><label className="label">Rôle</label>
          <select className="select" value={form.role} onChange={s("role")} data-testid="invite-role">
            <option value="admin_societe">Admin société</option>
            <option value="responsable_etablissement">Responsable établissement</option>
            <option value="consultation">Consultation (lecture seule)</option>
          </select>
        </div>
        <div><label className="label">Établissement</label>
          <select className="select" value={form.etablissement_id} onChange={s("etablissement_id")} data-testid="invite-etab">
            {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom} — {e.siret}</option>)}
          </select>
        </div>
        {form.role === "responsable_etablissement" && (
          <div><label className="label">Seuil d'engagement HT (€) <span className="text-[#3E4C59] font-normal">— vide = illimité</span></label>
            <input type="number" step="100" min="0" className="input" value={form.seuil_engagement_ht}
              onChange={s("seuil_engagement_ht")} placeholder="Ex. 10000" data-testid="invite-seuil" /></div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="invite-submit">{busy ? "…" : "Envoyer"}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function MemberEditModal({ member, etablissements, onClose, onSaved }) {
  const [form, setForm] = useState({
    role: member.role,
    etablissement_id: member.etablissement_id,
    seuil_engagement_ht: member.seuil_engagement_ht ?? "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { role: form.role, etablissement_id: form.etablissement_id };
      if (form.role === "responsable_etablissement" && form.seuil_engagement_ht !== "") payload.seuil_engagement_ht = Number(form.seuil_engagement_ht);
      await api.patch(`/organisations/memberships/${member.id}`, payload);
      toast.success("Mis à jour"); onSaved(); onClose();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <ModalShell title={`Modifier ${member.user_email}`} onClose={onClose}>
      <form onSubmit={submit} className="p-4 space-y-3">
        <div><label className="label">Rôle</label>
          <select className="select" value={form.role} onChange={s("role")}>
            <option value="admin_societe">Admin société</option>
            <option value="responsable_etablissement">Responsable établissement</option>
            <option value="consultation">Consultation</option>
          </select>
        </div>
        <div><label className="label">Établissement</label>
          <select className="select" value={form.etablissement_id} onChange={s("etablissement_id")}>
            {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
          </select>
        </div>
        {form.role === "responsable_etablissement" && (
          <div><label className="label">Seuil d'engagement HT (€)</label>
            <input type="number" step="100" min="0" className="input" value={form.seuil_engagement_ht}
              onChange={s("seuil_engagement_ht")} placeholder="Vide = illimité" /></div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
          <button type="submit" disabled={busy} className="btn-primary">{busy ? "…" : "Enregistrer"}</button>
        </div>
      </form>
    </ModalShell>
  );
}
